import { ReactNode, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import GlobalSearch from "@/pages/admin/components/GlobalSearch";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown, Flag, LogOut, Mail, Scale } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { POST_LOSS_ROUTES, ADMIN_ROUTES } from "@/shared/constants/paths";
import AlternativeLogoSymbol from "@/shared/components/logo/AlternativeLogoSymbol";

interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
  titleLink?: string;
  maxWidth?: "7xl" | "full";
  backgroundColor?: "default" | "stone-100";
  searchComponent?: ReactElement;
}

export default function AdminLayout({
  children,
  title,
  titleLink,
  maxWidth = "7xl",
  backgroundColor = "default",
  searchComponent,
}: AdminLayoutProps) {
  const navigate = useNavigate();

  // Fetch pending lawyer applications count
  const { data: pendingLawyersCount } = useQuery({
    queryKey: ["admin-pending-lawyers-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("users")
        .select("user_id", { count: "exact", head: true })
        .eq("customizations->lawyer_application->>status", "pending");

      if (error) return 0;
      return count || 0;
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  const bgClass =
    backgroundColor === "stone-100"
      ? "bg-stone-100"
      : "bg-gradient-to-br from-slate-50 via-white to-slate-50";

  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header with Search and Settings */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo and Title */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => navigate(titleLink || ADMIN_ROUTES.LAWYERS)}
            >
              <AlternativeLogoSymbol isDark={true} size={40} />
              <div className="h-8 w-px bg-slate-300"></div>
              <h1 className="text-lg font-medium text-slate-900 tracking-tight ml-3">
                {title || "Admin Dashboard"}
              </h1>
            </div>

            {/* Center: Global Search */}
            <div className="flex-1 max-w-2xl mx-8">{searchComponent || <GlobalSearch />}</div>

            {/* Right: Settings Dropdown */}
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
                className="h-9 px-4 text-sm font-medium text-red-600 hover:text-red-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Exit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 hover:bg-slate-100 rounded-lg transition-all duration-200"
                  >
                    <ChevronDown className="w-4 h-4 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-48 rounded-xl shadow-lg border-slate-200"
                >
                  <DropdownMenuItem
                    onClick={() => navigate(ADMIN_ROUTES.LAWYERS)}
                    className="text-sm rounded-lg"
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    Lawyers
                    {(pendingLawyersCount ?? 0) > 0 && (
                      <span className="ml-auto text-[10px] bg-amber-600 text-white min-w-4 h-4 inline-flex items-center justify-center rounded-full leading-none">
                        {pendingLawyersCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(ADMIN_ROUTES.FEATURE_FLAGS)}
                    className="text-sm rounded-lg"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Feature Flags
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(ADMIN_ROUTES.PIXEL_CONTENT)}
                    className="text-sm rounded-lg"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Pixel Content
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
                    className="text-sm rounded-lg text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Exit Admin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`${maxWidth === "full" ? "max-w-full" : "max-w-7xl"} mx-auto px-6 lg:px-8 py-8`}
      >
        {children}
      </div>
    </div>
  );
}
